import { FC, useEffect, useRef, useState } from 'react';
import { notification, Tooltip, Tag, Alert } from 'antd';
import { Modal, Search, Button, DataGrid } from '@hydrodigital/design-system';
import { DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import SelectLocation from '@/basicsComponents/Selects/SelectLocation';
import { HydraulicProfileData } from '@/domain/HydraulicProfiler/HydraulicProfiler';
import HydraulicProfilerService from '@/domain/HydraulicProfiler/HydraulicProfilerService';
import useDebounce from '@/hooks/useDebounce';

const hydraulicProfilerService = new HydraulicProfilerService();

const ProfilerTable: FC<{
	handleUpdateModelFiles: any;
	files: Array<HydraulicProfileData>;
	onChangePagination: any;
	pagination: any;
	setInputSearch: Function;
	setType: Function;
	selectedLocation: any;
	setSelectedLocation: Function;
	handleUpdateProfilerFiles: Function;
}> = ({
	files,
	onChangePagination,
	pagination,
	setInputSearch,
	selectedLocation,
	setSelectedLocation,
	handleUpdateProfilerFiles,
}) => {
	const navigate = useNavigate();
	const userId = localStorage.getItem('userId') || '';

	const selectLocationRef = useRef<any>(null);
	const isInternalChange = useRef(false);
	const [showProfilerDeleteModal, setShowProfilerDeleteModal] = useState(false);
	const [profilerSelect, setProfilerSelect] = useState<any | null>(null);
	const [searchValue, setSearchValue] = useState<string>('');
	const debouncedSearch = useDebounce(searchValue, 500);
	const [paginationState, setPaginationState] = useState({
		pageIndex: pagination.page - 1,
		pageSize: pagination.pageSize || 10,
	});

	// Sync from parent props to local state (only when parent changes externally)
	useEffect(() => {
		if (!isInternalChange.current) {
			setPaginationState({
				pageIndex: pagination.page - 1,
				pageSize: pagination.pageSize || 10,
			});
		}
		isInternalChange.current = false;
	}, [pagination.page, pagination.pageSize]);

	// Handle local pagination changes and notify parent
	const handlePaginationChange = (updater: any) => {
		const newState = typeof updater === 'function' ? updater(paginationState) : updater;
		isInternalChange.current = true;
		setPaginationState(newState);
		onChangePagination(newState.pageIndex + 1, newState.pageSize);
	};

	useEffect(() => {
		setInputSearch(debouncedSearch || null);
	}, [debouncedSearch]);

	const gridColumns = [
		{
			header: 'File Name',
			accessorKey: 'name',
			cell: ({ row }: any) => (
				<>
					<span className="table-data-gis">{row.original?.name}</span>
				</>
			),
		},
		{
			header: 'Network File',
			accessorKey: 'networkFile',
			cell: ({ cell }: any) => <span className="table-data-gis">{cell.getValue() || 'N/A'}</span>,
		},
		{
			header: 'Location',
			accessorKey: 'locationName',
			cell: ({ cell }: any) => <span className="table-data-gis">{cell.getValue()}</span>,
		},
		{
			header: 'Upload Status',
			accessorKey: 'uploadStatus',
			cell: ({ row }: any) => {
				const uploadStatus = row.original?.uploadStatus;
				const fontColor = '#232B36';
				let color = '#FFEECB';
				if (uploadStatus === 'COMPLETED') color = '#C0EFDE';
				if (uploadStatus === 'IN PROGRESS') color = '#FFEECB';
				if (uploadStatus === 'FAILED') color = '#FFD3D3';
				return (
					uploadStatus && (
						<Tag color={color} style={{ color: fontColor }}>
							{uploadStatus}
						</Tag>
					)
				);
			},
		},
		{
			header: 'Upload Status Message',
			accessorKey: 'uploadStatusMessage',
			cell: ({ row }: any) => (
				<span className="table-data-gis">{row.original?.uploadStatusMessage}</span>
			),
		},
		{
			header: 'Actions',
			accessorKey: 'profileId',
			cell: ({ row }: any) => {
				const { profileId, name } = row.original || {};
				return (
					<Tooltip title="Delete">
						<DeleteOutlined
							className="mr-1"
							style={{ fontSize: '1.5rem', cursor: 'pointer' }}
							onClick={() => {
								setProfilerSelect({ id: profileId, label: name });
								setShowProfilerDeleteModal(true);
							}}
							rev={undefined}
						/>
					</Tooltip>
				);
			},
		},
	];

	<Search
		size="small"
		placeholder="Search"
		onChange={(e) => {
			if (e.target.value == '') {
				setInputSearch(null);
			}
		}}
		onBlur={(e) => {
			console.log('On press');
			if (e.target) {
				const element = e.currentTarget as HTMLInputElement;
				setInputSearch(element.value);
			}
		}}
		onKeyDown={(e) => {
			if (e.target) {
				const element = e.currentTarget as HTMLInputElement;
				if (e.key === 'Enter') setInputSearch(element.value);
			}
		}}
		onClear={() => {
			setInputSearch(null);
		}}
		showClearButton
	/>;

	return (
		<div>
			<div className="flex justify-between">
				<div style={{ flexDirection: 'row', display: 'flex' }}>
					<Search
						size="small"
						placeholder="Search"
						value={searchValue}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setSearchValue(e.target.value);
						}}
						onClear={() => {
							setSearchValue('');
						}}
						showClearButton
					/>
					<SelectLocation
						ref={selectLocationRef}
						className="ml-3 rounded-md"
						userId={userId}
						onSelect={(value: any) => {
							setSelectedLocation(value);
						}}
					/>
				</div>
				<div>
					<Button
						size="small"
						style={{
							fontWeight: 700,
							fontSize: 14,
							backgroundColor: '#F7F9FA',
							color: '#434B56',
							border: '1px solid #C4CCD5',
						}}
						label="Upload Profiler File"
						className="mr-5 boton-inicio flex items-center"
						onClick={() => {
							navigate(`profilerfiles`);
						}}
					/>
				</div>
			</div>
			<DataGrid
					className="mt-3"
					data={files}
					columns={gridColumns}
					pagination={{
						enabled: true,
						manualPagination: true,
						rowCount: pagination.totalRows,
						defaultPageSize: 10,
						pageSizeOptions: [10, 20, 50],
					}}
					stickyHeader={true}
					maxHeight="calc(100vh - 330px)"
					paginationState={paginationState}
					onPaginationChange={handlePaginationChange}
				/>
				<Modal
					isOpen={showProfilerDeleteModal}
					size="s"
					closeOnOverlayClick={true}
					closeOnEscape={true}
					showCloseButton={true}
					onClose={() => setShowProfilerDeleteModal(false)}
				>
					<Modal.Header>
						<div className="flex column items-center">
							<span
								className="mb-2"
								style={{
									color: '#232B36',
									fontSize: '1.2rem',
									fontWeight: 'bold',
								}}
							>
								Profiler File delete?
							</span>
						</div>
					</Modal.Header>

					<Modal.Content>
						<div className="flex column items-center">
							<Tooltip title={profilerSelect?.label}>
								<span
									className="mb-3"
									style={{
										color: '#74788D',
										fontSize: '1rem',
										fontWeight: '600',
										textAlign: 'center',
										width: '100%',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										display: 'inline-block',
										maxWidth: '100%',
									}}
								>
									Are you sure to delete <br /> {profilerSelect?.label} <br />?
								</span>
							</Tooltip>

							<span>
								<Alert
									style={{
										border: 'none',
										background: 'rgba(255, 211, 211, 0.5)',
									}}
									message={
										<div
											style={{
												color: '#F46A6A',
												fontWeight: 'bolder',
											}}
										>
											Warning
										</div>
									}
									description={
										<div style={{ color: '#F46A6A' }}>
											By deleting this will be wiped off the map
										</div>
									}
									type="error"
								/>
							</span>
						</div>
					</Modal.Content>
					<div className="separator mb-2 mt-2" />
					<Modal.Footer>
						<div className="mr-3">
							<Button
								label="No"
								showIcon={false}
								style={{
									width: '85px',
									height: '40px',
									backgroundColor: '#F7F9FA',
									color: '#434B56',
									border: '1px solid #C4CCD5',
								}}
								onClick={() => setShowProfilerDeleteModal(false)}
							/>
						</div>
						<Button
							label="Yes"
							showIcon={false}
							style={{
								width: '85px',
								height: '40px',
								backgroundColor: '#00203C',
								color: 'white',
							}}
							onClick={async () => {
								console.log('profilerSelect', profilerSelect);
								if (profilerSelect && profilerSelect.id) {
									await hydraulicProfilerService.deleteProfile(profilerSelect?.id?.toString());
									notification.success({
										message: 'Successful deletion',
										description: 'Profiler file deleted successfully',
										placement: 'topRight',
									});
									setShowProfilerDeleteModal(false);
									if (pagination.page > 1) {
										onChangePagination(1);
									} else {
										handleUpdateProfilerFiles();
									}
								}
							}}
						/>
					</Modal.Footer>
				</Modal>
		</div>
	);
};

export default ProfilerTable;
